import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Slider,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { isEqual, isUndefined, omitBy } from "lodash";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext.tsx";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import {
  ConditionType,
  LOWEST_MINIMUM_COMPENSATION,
  PartyAgreements,
} from "../../shared/Agreements.ts";
import { percentage } from "../../shared/percentage.ts";
import { Metric } from "../general/DataAtoms.tsx";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared.ts";
import { SectionLabel } from "../general/SectionLabel.tsx";
import { GuideMarker, Marked } from "../guide/GuideMarker.tsx";
import { SegmentedControl } from "../general/SegmentedControl.tsx";
import { COLUMN_HEADING, QUIET_SLIDER } from "../general/tableStyles.ts";
import CfaOutcomeLadder, { stepColor } from "./CfaOutcomeLadder.tsx";
import {
  barristerCfaIsMoot,
  cfaTerms,
  chainOf,
  requiredBarristerFeeCover,
  requiredEngagement,
  Step,
  stepsOf,
  StepTerms,
  storedTriggerAmount,
} from "./cfaTerms.ts";

const CONDITION_TYPES = [
  ConditionType.minimumCompensationRecovered,
  ConditionType.minimumCompensationAwarded,
  ConditionType.maximumCompensationToPay,
];

/** How strongly the bar with the save buttons is tinted while changes are unsaved. */
const UNSAVED_TINT_ALPHA = 0.06;
const UNSAVED_BORDER_ALPHA = 0.3;

/** A remark on the terms next to it: what is off about them, or why they are fixed. */
const Notice = ({
  tone,
  children,
}: {
  tone: "info" | "warning";
  children: ReactNode;
}) => {
  const { theme } = useTheme();
  const color = theme.palette[tone];
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: "flex-start",
        color: color.dark,
        bgcolor: alpha(color.main, 0.12),
        borderRadius: 1.5,
        px: 1.5,
        py: 0.75,
      }}
    >
      <InfoOutlinedIcon fontSize="small" />
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {children}
      </Typography>
    </Stack>
  );
};

/**
 * Amount input that follows the value it is given. CurrencyTextField keeps
 * the text it was mounted with, so an amount changed from outside — a cancel,
 * a step removed above this one — would not show in it.
 */
const AmountField = ({
  value,
  min,
  label,
  onValueChange,
  disabled,
}: {
  value: number;
  min: number;
  label: string;
  onValueChange: (newValue: number) => void;
  disabled: boolean;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const format = useMemo(
    () => currency.createNumericFormat(min),
    [currency, min],
  );
  // the text while the field is edited: it may be empty or end in a decimal
  // separator, neither of which the value can express
  const [editedText, setEditedText] = useState<string>();

  return (
    <TextField
      size="small"
      name="amount"
      disabled={disabled}
      value={editedText ?? value}
      // An amount of nothing is cleared for typing. The number format calls
      // this a tick after the focus, when a first key may already have been
      // typed — text that is there is never replaced.
      onFocus={() =>
        setEditedText((typed) => typed ?? (value === 0 ? "" : undefined))
      }
      onBlur={() => setEditedText(undefined)}
      onChange={({ target }) => {
        setEditedText(target.value);
        const newValue = Number.parseFloat(target.value);
        onValueChange(Number.isNaN(newValue) ? 0 : newValue);
      }}
      slotProps={{
        input: { inputComponent: format },
        htmlInput: { "aria-label": label },
      }}
      sx={{
        maxWidth: theme.spacing(24),
        "& input": { ...TABULAR_NUMERALS, fontWeight: 600 },
      }}
    />
  );
};

function ConditionalFeeAgreementConfigurator<
  CFA extends
    | NonNullable<PartyAgreements["lawFirmCfa"]>
    | NonNullable<PartyAgreements["barristerCfa"]>,
>({
  label,
  agreement,
  agreements,
  savedAgreement,
  baseCost,
  barristerFees,
  baseCostNotes = [],
  onSave,
  onUpdate,
  saveOngoing,
}: {
  label: string;
  agreement: CFA;
  // all agreements of the party as they are being edited, this one included:
  // what one of them covers decides what the other can be
  agreements: PartyAgreements;
  // the agreement as the case holds it, undefined before its first save
  savedAgreement: CFA | undefined;
  // budgeted costs the agreement covers
  baseCost: number;
  // budgeted barrister fees, whether the agreement covers them or not
  barristerFees: number;
  // what the covered base costs assume, under their sum
  baseCostNotes?: string[];
  onSave: (newAgreement: CFA) => void;
  onUpdate: (newAgreement: CFA) => void;
  saveOngoing: boolean;
}) {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "agreement.conditionalFeeAgreement",
  });
  // the terms are shown as they are set and cannot be changed
  const { readOnly } = useCase();
  const [tab, setTab] = useState<"ladder" | "readsAs">("ladder");

  const { successFee, lowFee } = agreement;
  const { conditionType } = successFee;
  const toPay = conditionType === ConditionType.maximumCompensationToPay;
  const noWinFeePercentage = lowFee?.discountPercentage ?? 0;
  const terms = useMemo(
    () => cfaTerms({ successFee, lowFee }, baseCost),
    [successFee, lowFee, baseCost],
  );

  // a save drops undefined fields, so they are no difference to what is saved
  const edited = !isEqual(
    omitBy(agreement, isUndefined),
    omitBy(savedAgreement, isUndefined),
  );

  const steps = stepsOf(successFee);
  const updateSteps = (newSteps: Step[], newConditionType = conditionType) =>
    onUpdate({
      ...agreement,
      successFee: {
        conditionType: newConditionType,
        step: chainOf(
          newSteps.map((step) => ({
            ...step,
            triggerAmount: storedTriggerAmount(
              step.triggerAmount,
              newConditionType,
            ),
          })),
        ),
      },
    });
  const updateStep = (index: number, change: Partial<Step>) =>
    updateSteps(
      steps.map((step, stepIndex) =>
        stepIndex === index ? { ...step, ...change } : step,
      ),
    );

  /** The trigger values a step decides the fee for. */
  const rangeOf = ({ triggerAmount, nextTriggerAmount }: StepTerms) => {
    if (toPay) {
      if (triggerAmount === 0) return t("configurator.range.nothingToPay");
      // amounts to pay are stored below zero
      const upTo = currency.format(Math.abs(triggerAmount));
      return nextTriggerAmount === undefined
        ? t("configurator.range.upTo", { upTo })
        : t("configurator.range.aboveUpTo", {
            above: currency.format(Math.abs(nextTriggerAmount)),
            upTo,
          });
    }
    const from = currency.format(triggerAmount);
    return nextTriggerAmount === undefined
      ? t("configurator.range.from", { from })
      : t("configurator.range.between", {
          from,
          to: currency.format(nextTriggerAmount),
        });
  };
  const ranges = terms.steps.map(rangeOf);

  const warning = terms.unordered
    ? t(
        toPay
          ? "configurator.warning.unorderedToPay"
          : "configurator.warning.unordered",
      )
    : terms.ineffective
      ? t("configurator.warning.ineffective")
      : undefined;

  const totals = terms.steps.map(({ total }) => total);
  const lowestTotal = Math.min(...totals);
  const highestTotal = Math.max(...totals);

  const positive = theme.palette.data.positive;
  const divider = `1px solid ${theme.palette.divider}`;
  const section = { px: 3, pt: 2.25, pb: 2.5, borderBottom: divider } as const;
  // what the two agreements share, the guide marks on the law firm's
  const markedHere = (entry: string) =>
    "engagedBy" in agreement ? undefined : entry;
  const stepGrid = {
    display: "grid",
    gridTemplateColumns: `${theme.spacing(10.5)} 1fr ${theme.spacing(25)} ${theme.spacing(11)} ${theme.spacing(18)} ${theme.spacing(4.5)}`,
    gap: 1.5,
    alignItems: "center",
    px: 1.75,
  } as const;

  // what the agreement covers, and the wording of that for the "reads as" tab
  let coverage: ReactNode;
  let coverageClause: string;
  // the agreement switched on, in a way that goes with the party's other CFA
  let switchedOn: CFA;
  // set where the two CFAs were saved in a combination no longer offered
  let combinationNotice: ReactNode;
  if (!("includedDisbursements" in agreement)) {
    // barrister CFA
    const engagedBy = agreement.engagedBy ?? "party";
    // next to a law-firm CFA, who engages the barrister follows from its cover
    const required = requiredEngagement(agreements);
    const mismatch = required !== undefined && required !== engagedBy;
    // the two go together: neither can change while both are switched on
    const locked = required !== undefined && !mismatch;
    switchedOn = {
      ...agreement,
      active: true,
      engagedBy: required ?? engagedBy,
    };
    combinationNotice = barristerCfaIsMoot(agreements) ? (
      <Notice tone="warning">{t("configurator.precedence.moot")}</Notice>
    ) : (
      mismatch && (
        <Notice tone="info">{t("configurator.precedence.recharged")}</Notice>
      )
    );
    coverageClause = t(`configurator.readsAs.coverage.barrister.${engagedBy}`, {
      covered: currency.format(baseCost),
    });
    coverage = (
      <>
        <Metric
          label={t("configurator.coveredBaseCosts.barristerFees")}
          value={currency.format(baseCost)}
          caption={t("configurator.coveredBaseCosts.fromBudget")}
        />
        <Box sx={{ borderLeft: { md: divider }, pl: { md: 2.5 } }}>
          <Typography
            variant="caption"
            sx={{ display: "block", fontWeight: 600, color: "text.secondary" }}
          >
            {t("configurator.coveredBaseCosts.engagement")}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <Switch
              checked={engagedBy === "lawFirm"}
              disabled={locked || readOnly}
              onChange={(_, checked) =>
                onUpdate({
                  ...agreement,
                  engagedBy: checked ? "lawFirm" : "party",
                })
              }
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t(`barristerCfaEngagedBy.${engagedBy}`)}
            </Typography>
          </Stack>
          {locked && (
            <Notice tone="info">
              {t(`configurator.precedence.engagementLocked.${engagedBy}`)}
            </Notice>
          )}
        </Box>
      </>
    );
  } else {
    // law-firm CFA
    const { includedDisbursements } = agreement;
    const coversBarrister = includedDisbursements.includes("barristerFee");
    const withoutBarristerFees = includedDisbursements.filter(
      (type) => type !== "barristerFee",
    );
    // next to a barrister CFA, the cover follows from who engaged the barrister
    const requiredCover = requiredBarristerFeeCover(agreements);
    const mismatch =
      requiredCover !== undefined && requiredCover !== coversBarrister;
    // the two go together: neither can change while both are switched on
    const locked = requiredCover !== undefined && !mismatch;
    switchedOn = {
      ...agreement,
      active: true,
      includedDisbursements:
        requiredCover === undefined
          ? includedDisbursements
          : requiredCover
            ? [...withoutBarristerFees, "barristerFee"]
            : withoutBarristerFees,
    };
    const ownBaseCost = coversBarrister ? baseCost - barristerFees : baseCost;
    coverageClause = coversBarrister
      ? t("configurator.readsAs.coverage.withBarrister", {
          baseCosts: currency.format(ownBaseCost),
          barristerFees: currency.format(barristerFees),
          covered: currency.format(baseCost),
        })
      : t("configurator.readsAs.coverage.withoutBarrister", {
          baseCosts: currency.format(ownBaseCost),
          barristerFees: currency.format(barristerFees),
        });
    coverage = (
      <>
        <Metric
          label={t("configurator.coveredBaseCosts.baseCosts")}
          value={currency.format(ownBaseCost)}
          caption={t("configurator.coveredBaseCosts.fromBudget")}
        />
        <Metric
          label={t("configurator.coveredBaseCosts.barristerFees")}
          value={currency.format(barristerFees)}
          caption={t(
            coversBarrister
              ? "configurator.coveredBaseCosts.barristerFeesIncluded"
              : "configurator.coveredBaseCosts.barristerFeesExcluded",
          )}
          sx={
            coversBarrister
              ? undefined
              : {
                  "& .MuiTypography-h5": {
                    color: "text.disabled",
                    textDecoration: "line-through",
                  },
                }
          }
        />
        <Box sx={{ borderLeft: { md: divider }, pl: { md: 2.5 } }}>
          <Typography
            variant="caption"
            sx={{ display: "block", fontWeight: 600, color: "text.secondary" }}
          >
            {t("configurator.coveredBaseCosts.barristerFeesUnderCfa")}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <Switch
              checked={coversBarrister}
              disabled={locked || readOnly}
              onChange={(_, checked) =>
                onUpdate({
                  ...agreement,
                  includedDisbursements: checked
                    ? [...withoutBarristerFees, "barristerFee"]
                    : withoutBarristerFees,
                })
              }
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: positive.contrastText,
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  bgcolor: positive.main,
                  opacity: 1,
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: coversBarrister ? positive.dark : "text.disabled",
              }}
            >
              {t(
                coversBarrister
                  ? "barristerFeesCovered.yes"
                  : "barristerFeesCovered.no",
              )}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            {t("configurator.coveredBaseCosts.total")}{" "}
            <Box
              component="strong"
              sx={{ ...TABULAR_NUMERALS, color: "text.primary" }}
            >
              {currency.format(baseCost)}
            </Box>
          </Typography>
          {baseCostNotes.map((note) => (
            <Typography
              key={note}
              variant="caption"
              sx={{ display: "block", color: "text.secondary" }}
            >
              {note}
            </Typography>
          ))}
          {locked && (
            <Notice tone="info">
              {t(
                coversBarrister
                  ? "configurator.precedence.coverLocked.covered"
                  : "configurator.precedence.coverLocked.notCovered",
              )}
            </Notice>
          )}
          {mismatch &&
            (coversBarrister ? (
              <Notice tone="warning">
                {t("configurator.precedence.mootOnLawFirm")}
              </Notice>
            ) : (
              <Notice tone="info">
                {t("configurator.precedence.rechargedOnLawFirm")}
              </Notice>
            ))}
        </Box>
      </>
    );
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          px: 3,
          py: 2.25,
          borderBottom: agreement.active ? divider : undefined,
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Switch
            checked={agreement.active}
            onChange={(_, active) =>
              onSave(active ? switchedOn : { ...agreement, active })
            }
            disabled={saveOngoing || readOnly}
          />
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-.01em",
                  color: agreement.active ? undefined : "text.disabled",
                }}
              >
                {label}
              </Typography>
              <GuideMarker
                entry={"engagedBy" in agreement ? "barrister" : "lawFirmCfa"}
              />
            </Stack>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("configurator.subtitle")}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {agreement.active && (
        <Box
          sx={{
            pointerEvents: saveOngoing ? "none" : "auto",
            opacity: saveOngoing ? 0.6 : 1,
          }}
        >
          {combinationNotice && (
            <Box sx={{ px: 3, py: 1.5, borderBottom: divider }}>
              {combinationNotice}
            </Box>
          )}

          {/* Covered base costs */}
          <Box sx={section}>
            <Marked entry={markedHere("covered")}>
              <SectionLabel>
                {t("configurator.coveredBaseCosts.title")}
              </SectionLabel>
            </Marked>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md:
                    "engagedBy" in agreement ? "1fr 1.15fr" : "1fr 1fr 1.15fr",
                },
                gap: 2.5,
                alignItems: "center",
                mt: 1.75,
              }}
            >
              {coverage}
            </Box>
          </Box>

          {/* If the case is lost */}
          <Box sx={section}>
            <Marked entry={markedHere("lost")}>
              <SectionLabel>{t("configurator.lost.title")}</SectionLabel>
            </Marked>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: `${theme.spacing(37.5)} 1fr`,
                },
                gap: 3.25,
                alignItems: "center",
                mt: 1.75,
              }}
            >
              <Box>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "baseline",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "text.secondary" }}
                  >
                    {t("configurator.lost.noWinFee")}
                  </Typography>
                  <Typography sx={{ ...TABULAR_NUMERALS, fontWeight: 700 }}>
                    {percentage.format(noWinFeePercentage)}
                  </Typography>
                </Stack>
                <Slider
                  sx={QUIET_SLIDER}
                  color="primary"
                  value={Math.round(noWinFeePercentage * 100)}
                  disabled={readOnly}
                  aria-label={t("configurator.lost.noWinFee")}
                  onChange={(_, value) =>
                    typeof value === "number" &&
                    onUpdate({
                      ...agreement,
                      // no fee at all is an agreement without a low fee
                      lowFee:
                        value > 0
                          ? { discountPercentage: value / 100 }
                          : undefined,
                    })
                  }
                  min={0}
                  max={100}
                />
                <Stack direction="row" sx={{ justifyContent: "space-between" }}>
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    {t("configurator.lost.noWinNoFee", {
                      percentage: percentage.format(0),
                    })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    {percentage.format(1)}
                  </Typography>
                </Stack>
              </Box>
              <Stack
                direction="row"
                spacing={3.5}
                useFlexGap
                sx={{ flexWrap: "wrap" }}
              >
                <Metric
                  label={t("configurator.lost.guaranteed")}
                  value={currency.format(terms.noWinFee)}
                />
                <Metric
                  label={t("configurator.lost.lostBaseCosts")}
                  value={currency.format(terms.atRisk)}
                />
                <Metric
                  label={t("configurator.lost.successFeeAppliesTo")}
                  value={percentage.format(1 - noWinFeePercentage)}
                />
              </Stack>
            </Box>
          </Box>

          {/* Success conditions */}
          <Box sx={section}>
            <Stack
              direction="row"
              spacing={2}
              useFlexGap
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                mb: 1.5,
              }}
            >
              <Marked entry={markedHere("success")}>
                <SectionLabel>{t("section.success")}</SectionLabel>
              </Marked>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Marked entry={markedHere("trigger")}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("configurator.triggeredBy")}
                  </Typography>
                </Marked>
                <SegmentedControl
                  disabled={readOnly}
                  value={conditionType}
                  options={CONDITION_TYPES.map((type) => ({
                    value: type,
                    label: t(`successConditionType.${type}`),
                  }))}
                  onChange={(type) => updateSteps(steps, type)}
                />
              </Stack>
            </Stack>

            <Box
              sx={{
                border: divider,
                borderRadius: 2.25,
                overflowX: "auto",
              }}
            >
              <Box sx={{ minWidth: theme.spacing(95) }}>
                <Box
                  sx={{
                    ...stepGrid,
                    py: 1,
                    bgcolor: theme.palette.action.hover,
                    borderBottom: divider,
                  }}
                >
                  <Typography variant="caption" sx={COLUMN_HEADING}>
                    {t("configurator.column.step")}
                  </Typography>
                  <Typography variant="caption" sx={COLUMN_HEADING}>
                    {t(`successCondition.${conditionType}`)}
                  </Typography>
                  <Typography variant="caption" sx={COLUMN_HEADING}>
                    {t("successFee")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ ...COLUMN_HEADING, textAlign: "right" }}
                  >
                    {t("configurator.column.rate")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ ...COLUMN_HEADING, textAlign: "right" }}
                  >
                    {t("configurator.column.uplift")}
                  </Typography>
                  <Box />
                </Box>

                {terms.steps.map((step, index) => (
                  <Box
                    key={index}
                    sx={{ ...stepGrid, py: 1.5, borderBottom: divider }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ alignItems: "center" }}
                    >
                      <Box
                        sx={{
                          width: theme.spacing(1),
                          height: theme.spacing(1),
                          borderRadius: "2px",
                          bgcolor: stepColor(theme, index),
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t("successCondition.step", { count: index + 1 })}
                      </Typography>
                    </Stack>
                    <Box>
                      <AmountField
                        value={Math.abs(step.triggerAmount)}
                        // nothing to pay is a threshold, nothing recovered
                        // or awarded is a lost case
                        min={toPay ? 0 : LOWEST_MINIMUM_COMPENSATION}
                        label={t(`successCondition.${conditionType}`)}
                        disabled={readOnly}
                        onValueChange={(triggerAmount) =>
                          updateStep(index, { triggerAmount })
                        }
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "text.disabled",
                          mt: 0.5,
                        }}
                      >
                        {ranges[index]}
                      </Typography>
                    </Box>
                    <Slider
                      sx={QUIET_SLIDER}
                      color="primary"
                      value={Math.round(step.successFeePercentage * 100)}
                      disabled={readOnly}
                      aria-label={t("successFeePercentage")}
                      onChange={(_, value) =>
                        typeof value === "number" &&
                        updateStep(index, { successFeePercentage: value / 100 })
                      }
                      min={0}
                      max={100}
                    />
                    <Box sx={{ textAlign: "right" }}>
                      <Typography sx={{ ...TABULAR_NUMERALS, fontWeight: 700 }}>
                        {percentage.format(step.successFeePercentage)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.disabled" }}
                      >
                        {t("configurator.ofAtRisk")}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          ...TABULAR_NUMERALS,
                          fontWeight: 600,
                          color: positive.dark,
                        }}
                      >
                        +{currency.format(step.uplift)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ ...TABULAR_NUMERALS, color: "text.disabled" }}
                      >
                        {t("configurator.stepTotal", {
                          value: currency.format(step.total),
                        })}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      {steps.length > 1 && (
                        <Tooltip title={t("configurator.removeStep")}>
                          {/* the tooltip needs an element that fires events,
                              which a disabled button does not */}
                          <span>
                            <IconButton
                              size="small"
                              aria-label={t("configurator.removeStep")}
                              disabled={readOnly}
                              onClick={() =>
                                updateSteps(
                                  steps.filter(
                                    (_, stepIndex) => stepIndex !== index,
                                  ),
                                )
                              }
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                ))}

                <Stack
                  direction="row"
                  spacing={1.75}
                  useFlexGap
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    px: 1.75,
                    py: 1.25,
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={readOnly}
                    onClick={() =>
                      updateSteps([...steps, steps[steps.length - 1]])
                    }
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    {t("addSuccessStep")}
                  </Button>
                  {warning && <Notice tone="warning">{warning}</Notice>}
                </Stack>
              </Box>
            </Box>
          </Box>

          {/* What the terms come to, whichever tab is open */}
          <Stack
            direction="row"
            spacing={3.25}
            useFlexGap
            sx={{
              alignItems: "center",
              flexWrap: "wrap",
              px: 3,
              py: 2,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            {[
              {
                label: t("configurator.summary.lost"),
                value: currency.format(terms.noWinFee),
              },
              {
                label: t("configurator.summary.won"),
                value:
                  lowestTotal === highestTotal
                    ? currency.format(lowestTotal)
                    : t("configurator.summary.range", {
                        minimum: currency.format(lowestTotal),
                        maximum: currency.format(highestTotal),
                      }),
              },
            ].map(({ label, value }, index) => (
              <Box
                key={label}
                sx={
                  index > 0
                    ? {
                        pl: 3.25,
                        borderLeft: `1px solid ${alpha(theme.palette.primary.contrastText, 0.16)}`,
                      }
                    : undefined
                }
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 600,
                    letterSpacing: ".05em",
                    textTransform: "uppercase",
                    opacity: 0.6,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ ...TABULAR_NUMERALS, fontWeight: 700 }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={{
              px: 3,
              borderBottom: divider,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
            }}
          >
            <Tab value="ladder" label={t("configurator.tab.ladder")} />
            <Tab value="readsAs" label={t("configurator.tab.readsAs")} />
          </Tabs>

          {tab === "ladder" && (
            <CfaOutcomeLadder
              terms={terms}
              baseCost={baseCost}
              noWinFeePercentage={noWinFeePercentage}
              triggerLabel={t(`successConditionType.${conditionType}`)}
              ranges={ranges}
            />
          )}

          {tab === "readsAs" && (
            <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
              <Typography sx={{ maxWidth: "74ch", lineHeight: 1.66 }}>
                {coverageClause}{" "}
                {terms.noWinFee > 0
                  ? t("configurator.readsAs.lostWithFee", {
                      noWinFee: currency.format(terms.noWinFee),
                      percentage: percentage.format(noWinFeePercentage),
                      atRisk: currency.format(terms.atRisk),
                    })
                  : t("configurator.readsAs.lostWithoutFee", {
                      covered: currency.format(baseCost),
                    })}{" "}
                {t(`configurator.readsAs.won.${conditionType}`, {
                  atRisk: currency.format(terms.atRisk),
                })}
              </Typography>
              {terms.steps.map((step, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    maxWidth: "74ch",
                    mt: 1,
                    pl: 1.75,
                    borderLeft: `2px solid ${stepColor(theme, index)}`,
                    color: "text.secondary",
                  }}
                >
                  {t("configurator.readsAs.step", {
                    step: t("successCondition.step", { count: index + 1 }),
                    range: ranges[index],
                    percentage: percentage.format(step.appliedPercentage),
                    uplift: currency.format(step.uplift),
                    total: currency.format(step.total),
                  })}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Everything above shows the draft: here it is stored or dropped */}
      {agreement.active && (
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            px: 3,
            py: 1.75,
            borderTop: `1px solid ${
              edited
                ? alpha(theme.palette.warning.main, UNSAVED_BORDER_ALPHA)
                : theme.palette.divider
            }`,
            bgcolor: edited
              ? alpha(theme.palette.warning.main, UNSAVED_TINT_ALPHA)
              : undefined,
          }}
        >
          {edited && (
            <Stack
              direction="row"
              spacing={1.25}
              sx={{ alignItems: "center", color: theme.palette.warning.dark }}
            >
              <Box
                sx={{
                  width: theme.spacing(1),
                  height: theme.spacing(1),
                  borderRadius: "50%",
                  bgcolor: theme.palette.warning.main,
                  flex: "none",
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("configurator.footer.unsaved")}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" spacing={1.5} sx={{ ml: "auto" }}>
            <Button
              color="inherit"
              // back to the agreement as the case holds it
              onClick={() => savedAgreement && onUpdate(savedAgreement)}
              disabled={
                !edited ||
                saveOngoing ||
                savedAgreement === undefined ||
                readOnly
              }
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={() => onSave(agreement)}
              // an agreement the case does not hold yet counts as edited
              disabled={!edited || readOnly}
              loading={edited && saveOngoing}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              {t("configurator.footer.save")}
            </Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
}

export default ConditionalFeeAgreementConfigurator;
